import { useMemo } from 'react'
import dayjs from 'dayjs'

import { Consultation, ConsultationStatus } from '@/core/models/consultation.model'
import { useGetConsultationsQuery } from '@/core/services/consultation'
import { useListWaitingRoomsQuery } from '@/core/services/waiting-room'

export type WaitingPatient = {
  consultationId: string
  patientLabel: string
  waitingRoomName?: string
  since: string
  withDoctor: boolean
}

const REFRESH_MS = 15_000

/** Today's patients this reception account put in a waiting room and who are still waiting or with the doctor. */
export function useWaitingPatients() {
  const startOfDay = useMemo(() => dayjs().startOf('day').toISOString(), [])
  const { data, isLoading } = useGetConsultationsQuery(
    {
      query: {
        status: { $in: [ConsultationStatus.Scheduled, ConsultationStatus.Active] },
        waitingRoom: { $ne: null },
        startDate: { $gte: startOfDay },
      },
      options: { limit: 50, skip: 0, order: 'ASC' },
    },
    { pollingInterval: REFRESH_MS, refetchOnMountOrArgChange: true }
  )
  const { data: rooms } = useListWaitingRoomsQuery({ query: {}, options: { limit: 0, skip: 0 } })

  const patients = useMemo<WaitingPatient[]>(() => {
    const roomNames = new Map((rooms?.items ?? []).map((room) => [room._id, room.name]))
    return (data?.[0] ?? []).map((consultation: Consultation) => ({
      consultationId: consultation._id,
      patientLabel: `${consultation.patient?.userInfo?.name ?? ''} ${consultation.patient?.userInfo?.surname ?? ''}`.trim(),
      waitingRoomName: consultation.waitingRoom ? roomNames.get(consultation.waitingRoom) : undefined,
      since: consultation.startDate,
      withDoctor: consultation.status === ConsultationStatus.Active,
    }))
  }, [data, rooms])

  return { patients, isLoading }
}
